/**
 * Algolia resource search (Alpine component) for the /search-demo test page.
 *
 * Queries Algolia's REST API directly with the search-only key; no Algolia
 * search library. Markup lives in templates/_search-demo/index.twig.
 *
 * Click and view events go to Algolia Insights through search-insights, which
 * keeps an anonymous user token in the _ALGOLIA cookie. Each hit carries the
 * queryID and absolute position of the response it came from, since "Load
 * more" mixes hits from several responses.
 *
 * Expects data attributes on the x-data element:
 *   data-app-id, data-search-key, data-index
 */
import aa from 'search-insights';

const HITS_PER_PAGE = 9;

export default () => ({
	query: '',
	hits: [],
	total: null,
	page: 0,
	pages: 0,
	loading: false,
	error: false,
	requestId: 0,

	get hasMore() {
		return !this.error && this.page + 1 < this.pages;
	},

	init() {
		const { appId, searchKey, index } = this.$el.dataset;
		this.index = index;
		aa('init', { appId, apiKey: searchKey, useCookie: true });

		this.endpoint = `https://${appId}-dsn.algolia.net/1/indexes/${encodeURIComponent(index)}/query`;
		this.headers = {
			'X-Algolia-Application-Id': appId,
			'X-Algolia-API-Key': searchKey,
			'Content-Type': 'application/json',
			// Ties searches to the same user as the Insights events
			'X-Algolia-UserToken': String(aa('getUserToken')),
		};

		this.query = new URLSearchParams(window.location.search).get('q') ?? '';
		this.$watch('query', () => {
			this.syncUrl();
			this.search();
		});
		this.search();
	},

	search() {
		return this.fetchPage(0);
	},

	loadMore() {
		return this.fetchPage(this.page + 1);
	},

	async fetchPage(page) {
		// Ignore responses that arrive after a newer request started (fast typing)
		const requestId = ++this.requestId;
		this.loading = true;
		this.error = false;

		try {
			const response = await fetch(this.endpoint, {
				method: 'POST',
				headers: this.headers,
				body: JSON.stringify({
					query: this.query.trim(),
					page,
					hitsPerPage: HITS_PER_PAGE,
					clickAnalytics: true,
				}),
			});
			if (!response.ok) throw new Error(`Algolia responded ${response.status}`);
			const data = await response.json();
			if (requestId !== this.requestId) return;

			const hits = data.hits.map((hit, i) => ({
				...hit,
				__queryID: data.queryID,
				__position: data.page * data.hitsPerPage + i + 1,
			}));
			this.hits = page === 0 ? hits : [...this.hits, ...hits];
			this.total = data.nbHits;
			this.page = data.page;
			this.pages = data.nbPages;
			this.trackView(hits);
		} catch (e) {
			if (requestId !== this.requestId) return;
			console.error(e);
			this.error = true;
			if (page === 0) {
				this.hits = [];
				this.total = null;
			}
		} finally {
			if (requestId === this.requestId) this.loading = false;
		}
	},

	trackView(hits) {
		const objectIDs = hits.map((hit) => hit.objectID);
		if (!objectIDs.length) return;
		aa('viewedObjectIDs', { index: this.index, eventName: 'Resources Viewed', objectIDs });
	},

	trackClick(hit) {
		if (!hit.__queryID) return;
		aa('clickedObjectIDsAfterSearch', {
			index: this.index,
			eventName: 'Resource Clicked',
			queryID: hit.__queryID,
			objectIDs: [hit.objectID],
			positions: [hit.__position],
		});
	},

	// Keep ?q= in sync so a search can be shared or reloaded
	syncUrl() {
		const url = new URL(window.location.href);
		const q = this.query.trim();
		if (q) url.searchParams.set('q', q);
		else url.searchParams.delete('q');
		window.history.replaceState({}, '', url);
	},
});
